import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../api/client';
import { usePageAuthorization } from '../hooks/usePageAuthorization';
import { useEvents } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';

// Task 1.1: VenueEvent interface with full event fields
interface VenueEvent {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  maxCapacity: number;
}

interface VenueItem {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  capacity: number;
  latitude?: number | null;
  longitude?: number | null;
  website?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  parkingInfo?: string | null;
  parkingImageUrl?: string | null;
  events?: VenueEvent[];
  _count?: { events: number };
}

interface VenueFormData {
  name: string;
  address: string;
  city: string;
  state: string;
  capacity: string;
  latitude: string;
  longitude: string;
  website: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  parkingInfo: string;
  parkingImageUrl: string;
}

const emptyForm: VenueFormData = {
  name: '',
  address: '',
  city: '',
  state: '',
  capacity: '',
  latitude: '',
  longitude: '',
  website: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  parkingInfo: '',
  parkingImageUrl: '',
};

// Task 2.2: Date/time formatting helpers
function formatEventDate(isoDate: string): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const datePart = isoDate.split('T')[0];
  const [year, month, day] = datePart.split('-');
  const monthIndex = parseInt(month, 10) - 1;
  const dayNum = parseInt(day, 10);
  return `${months[monthIndex]} ${dayNum}, ${year}`;
}

function formatEventTime(isoDateTime: string): string {
  const timePart = isoDateTime.split('T')[1];
  if (!timePart) return '';
  const [hourStr, minuteStr] = timePart.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  return `${hour}:${minute} ${ampm}`;
}

// ── Venue intelligence ────────────────────────────────────────────────────
// Org-perspective stats derived entirely from the events already returned by
// GET /venues. No backend call needed.

interface VenueStats {
  total: number;
  past: number;
  upcoming: number;
  nextEvent: VenueEvent | null; // soonest future event
  lastEvent: VenueEvent | null; // most recent past event
  /** Avg of event maxCapacity across events (reach planned at this venue). */
  avgEventCapacity: number;
  /** avgEventCapacity / venue.capacity, as a 0..1+ "fit" ratio (may exceed 1). */
  fitRatio: number | null;
}

/** Parse a stored event date (YYYY-MM-DD...) as a LOCAL date — no UTC shift. */
function parseEventDateLocal(isoDate: string): Date {
  const datePart = isoDate.split('T')[0];
  const [y, m, d] = datePart.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function computeVenueStats(venue: VenueItem): VenueStats {
  const events = venue.events ?? [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const past: VenueEvent[] = [];
  const upcoming: VenueEvent[] = [];
  for (const e of events) {
    const d = parseEventDateLocal(e.date);
    if (d.getTime() >= today.getTime()) upcoming.push(e);
    else past.push(e);
  }
  // Soonest upcoming first; most recent past first.
  upcoming.sort((a, b) => parseEventDateLocal(a.date).getTime() - parseEventDateLocal(b.date).getTime());
  past.sort((a, b) => parseEventDateLocal(b.date).getTime() - parseEventDateLocal(a.date).getTime());

  const capacities = events.map((e) => e.maxCapacity || 0).filter((c) => c > 0);
  const avgEventCapacity = capacities.length
    ? Math.round(capacities.reduce((s, c) => s + c, 0) / capacities.length)
    : 0;
  const fitRatio = venue.capacity > 0 && avgEventCapacity > 0
    ? avgEventCapacity / venue.capacity
    : null;

  return {
    total: events.length,
    past: past.length,
    upcoming: upcoming.length,
    nextEvent: upcoming[0] ?? null,
    lastEvent: past[0] ?? null,
    avgEventCapacity,
    fitRatio,
  };
}

/** Human "days until / days ago" relative to today, from a stored event date. */
function relativeDays(isoDate: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = parseEventDateLocal(isoDate);
  const diff = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff === -1) return 'yesterday';
  if (diff > 1) return `in ${diff} days`;
  return `${Math.abs(diff)} days ago`;
}

/**
 * Capacity-fit descriptor: tells the org whether the venue is well-sized for
 * the events they actually book there. Returns null when there's nothing to
 * compare (no events or no venue capacity).
 */
function fitDescriptor(fitRatio: number | null): { label: string; bg: string; fg: string } | null {
  if (fitRatio === null) return null;
  const pct = Math.round(fitRatio * 100);
  if (fitRatio > 1.0) {
    return { label: `⚠ Over capacity (${pct}%)`, bg: '#fdecea', fg: '#b3261e' };
  }
  if (fitRatio >= 0.75) {
    return { label: `Great fit (${pct}%)`, bg: '#e8f5e9', fg: '#1a6b3c' };
  }
  if (fitRatio >= 0.4) {
    return { label: `Room to grow (${pct}%)`, bg: '#fff8e1', fg: '#8a6d00' };
  }
  return { label: `Under-utilized (${pct}%)`, bg: '#eef2ff', fg: '#3730a3' };
}

const statStyles: Record<string, React.CSSProperties> = {
  strip: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.4rem',
    marginTop: '0.6rem',
    alignItems: 'center',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    padding: '0.25rem 0.6rem',
    borderRadius: '999px',
    fontSize: '0.72rem',
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: 'nowrap' as const,
  },
  pillNum: { fontSize: '0.78rem' },
  insight: {
    marginTop: '0.5rem',
    fontSize: '0.78rem',
    color: '#4b5563',
    lineHeight: 1.5,
  },
};

/** Compact, org-facing stat strip shown on each venue card. */
function VenueStatStrip({ venue }: { venue: VenueItem }) {
  const s = computeVenueStats(venue);
  const fit = fitDescriptor(s.fitRatio);

  // Brand-new venue with no history yet.
  if (s.total === 0) {
    return (
      <div style={statStyles.strip}>
        <span style={{ ...statStyles.pill, backgroundColor: '#f3f4f6', color: '#6b7280' }}>
          ✨ No events yet
        </span>
      </div>
    );
  }

  return (
    <div>
      <div style={statStyles.strip}>
        <span style={{ ...statStyles.pill, backgroundColor: '#e8f0fe', color: '#1a56db' }} title="Upcoming events booked here">
          <span style={statStyles.pillNum}>{s.upcoming}</span> upcoming
        </span>
        <span style={{ ...statStyles.pill, backgroundColor: '#ecfdf3', color: '#1a6b3c' }} title="Events already hosted here">
          <span style={statStyles.pillNum}>{s.past}</span> hosted
        </span>
        {s.avgEventCapacity > 0 && (
          <span style={{ ...statStyles.pill, backgroundColor: '#f5f3ff', color: '#6d28d9' }} title="Average planned attendance across events at this venue">
            👥 {s.avgEventCapacity.toLocaleString()} avg
          </span>
        )}
        {fit && (
          <span style={{ ...statStyles.pill, backgroundColor: fit.bg, color: fit.fg }} title="How well your typical event fills this venue's capacity">
            {fit.label}
          </span>
        )}
      </div>
      <div style={statStyles.insight}>
        {s.nextEvent ? (
          <>🗓️ Next: <strong>{s.nextEvent.name}</strong> · {formatEventDate(s.nextEvent.date)} ({relativeDays(s.nextEvent.date)})</>
        ) : s.lastEvent ? (
          <>🕗 Last hosted: <strong>{s.lastEvent.name}</strong> · {formatEventDate(s.lastEvent.date)} ({relativeDays(s.lastEvent.date)})</>
        ) : null}
      </div>
    </div>
  );
}

// Task 2.1: VenueDetailPanel component
function VenueDetailPanel({ venue, onPrint }: { venue: VenueItem; onPrint: () => void }) {
  return (
    <div style={detailStyles.panel}>
      <div style={detailStyles.header}>
        <h3 style={detailStyles.venueName}>{venue.name}</h3>
        <button
          style={detailStyles.printBtn}
          onClick={(e) => { e.stopPropagation(); onPrint(); }}
        >
          🖨️ Print
        </button>
      </div>

      <div style={detailStyles.infoGrid}>
        <div><strong>Address:</strong> {venue.address}</div>
        <div><strong>City:</strong> {venue.city}</div>
        <div><strong>State:</strong> {venue.state}</div>
        <div><strong>Capacity:</strong> {venue.capacity.toLocaleString()}</div>
      </div>

      <h4 style={detailStyles.eventsTitle}>Scheduled Events</h4>
      {venue.events && venue.events.length > 0 ? (
        <table style={detailStyles.table}>
          <thead>
            <tr>
              <th style={detailStyles.th}>Event Name</th>
              <th style={detailStyles.th}>Date</th>
              <th style={detailStyles.th}>Start</th>
              <th style={detailStyles.th}>End</th>
              <th style={detailStyles.th}>Capacity</th>
            </tr>
          </thead>
          <tbody>
            {venue.events.map((event) => (
              <tr key={event.id}>
                <td style={detailStyles.td}>{event.name}</td>
                <td style={detailStyles.td}>{formatEventDate(event.date)}</td>
                <td style={detailStyles.td}>{formatEventTime(event.startTime)}</td>
                <td style={detailStyles.td}>{formatEventTime(event.endTime)}</td>
                <td style={detailStyles.td}>{event.maxCapacity.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={detailStyles.noEvents}>No events scheduled at this venue.</p>
      )}
    </div>
  );
}

// Task 5.1: PrintableVenueDetail component
function PrintableVenueDetail({ venue }: { venue: VenueItem }) {
  return (
    <div id="printable-venue-detail" style={{ padding: '2rem' }}>
      <h2 style={{ color: '#1a6b3c', marginBottom: '0.5rem' }}>{venue.name}</h2>
      <p style={{ margin: '0.25rem 0' }}><strong>Address:</strong> {venue.address}</p>
      <p style={{ margin: '0.25rem 0' }}><strong>City:</strong> {venue.city}</p>
      <p style={{ margin: '0.25rem 0' }}><strong>State:</strong> {venue.state}</p>
      <p style={{ margin: '0.25rem 0' }}><strong>Capacity:</strong> {venue.capacity.toLocaleString()}</p>

      <h3 style={{ marginTop: '1.5rem', marginBottom: '0.75rem' }}>Scheduled Events</h3>
      {venue.events && venue.events.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ borderBottom: '2px solid #333', padding: '0.5rem', textAlign: 'left' }}>Event Name</th>
              <th style={{ borderBottom: '2px solid #333', padding: '0.5rem', textAlign: 'left' }}>Date</th>
              <th style={{ borderBottom: '2px solid #333', padding: '0.5rem', textAlign: 'left' }}>Start</th>
              <th style={{ borderBottom: '2px solid #333', padding: '0.5rem', textAlign: 'left' }}>End</th>
              <th style={{ borderBottom: '2px solid #333', padding: '0.5rem', textAlign: 'left' }}>Capacity</th>
            </tr>
          </thead>
          <tbody>
            {venue.events.map((event) => (
              <tr key={event.id}>
                <td style={{ borderBottom: '1px solid #ddd', padding: '0.5rem' }}>{event.name}</td>
                <td style={{ borderBottom: '1px solid #ddd', padding: '0.5rem' }}>{formatEventDate(event.date)}</td>
                <td style={{ borderBottom: '1px solid #ddd', padding: '0.5rem' }}>{formatEventTime(event.startTime)}</td>
                <td style={{ borderBottom: '1px solid #ddd', padding: '0.5rem' }}>{formatEventTime(event.endTime)}</td>
                <td style={{ borderBottom: '1px solid #ddd', padding: '0.5rem' }}>{event.maxCapacity.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No events scheduled at this venue.</p>
      )}
    </div>
  );
}

const detailStyles: Record<string, React.CSSProperties> = {
  panel: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderTop: 'none',
    borderRadius: '0 0 6px 6px',
    padding: '1.25rem',
    marginBottom: '0.75rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  venueName: {
    color: '#1a6b3c',
    fontSize: '1.25rem',
    margin: 0,
  },
  printBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.5rem',
    marginBottom: '1.25rem',
    fontSize: '0.9rem',
    color: '#333',
  },
  eventsTitle: {
    color: '#0f2d1f',
    fontSize: '1rem',
    marginBottom: '0.75rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.5rem',
    borderBottom: '2px solid #e0e0e0',
    color: '#555',
    fontWeight: 600,
  },
  td: {
    padding: '0.5rem',
    borderBottom: '1px solid #f0f0f0',
    color: '#333',
  },
  noEvents: {
    color: '#888',
    fontStyle: 'italic' as const,
    fontSize: '0.9rem',
  },
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '2rem',
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: {
    color: '#1a6b3c',
    fontSize: '1.75rem',
    margin: 0,
  },
  addButton: {
    padding: '0.6rem 1.2rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  venueCard: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    cursor: 'pointer',
  },
  venueHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  venueName: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: '#0f2d1f',
    marginBottom: '0.25rem',
  },
  venueMeta: {
    fontSize: '0.85rem',
    color: '#555',
    marginBottom: '0.25rem',
  },
  badge: {
    display: 'inline-block',
    backgroundColor: '#e8f5e9',
    color: '#1a6b3c',
    padding: '0.2rem 0.5rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  editBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#c9a84c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  useBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
  },
  currentVenueBadge: {
    padding: '0.35rem 0.7rem',
    backgroundColor: '#e8f5e9',
    color: '#1a6b3c',
    border: '1px solid #1a6b3c',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 700,
    whiteSpace: 'nowrap' as const,
  },
  deleteBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '2rem',
    width: '100%',
    maxWidth: '680px',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  modalTitle: {
    color: '#1a6b3c',
    marginBottom: '1.25rem',
    fontSize: '1.25rem',
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
  },
  submitBtn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  cancelBtn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#eee',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  warning: {
    color: '#e65100',
    backgroundColor: '#fff3e0',
    padding: '0.75rem',
    borderRadius: '4px',
    marginTop: '1rem',
    fontSize: '0.85rem',
  },
};

export function VenuesPage() {
  const { canEdit: pageCanEdit } = usePageAuthorization('venues');
  const { user } = useAuth();
  // Managing an org's own venues is core functionality for its operational
  // roles, not a configurable permission. A Customer Editor/Super User (or
  // higher) can always add/edit/delete venues; Page Access Control can still
  // grant edit to lower/custom roles on top of this.
  const canEdit = pageCanEdit || isAtLeast(user?.role ?? '', 'CustomerEditor');
  const { selectedEvent, selectedEventId, refresh: refreshEvents } = useEvents();
  const [venues, setVenues] = useState<VenueItem[]>([]);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<VenueFormData>(emptyForm);
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);

  // Task 1.1: Expansion and print state
  const [expandedVenueId, setExpandedVenueId] = useState<string | null>(null);
  const [printingVenueId, setPrintingVenueId] = useState<string | null>(null);

  // Task 1.1: Toggle expansion function
  function toggleVenueExpansion(venueId: string) {
    setExpandedVenueId((prev) => (prev === venueId ? null : venueId));
  }

  // Task 5.2: handlePrint function
  function handlePrint(venueId: string) {
    setPrintingVenueId(venueId);
    setTimeout(() => {
      window.print();
    }, 100);
  }

  // Task 5.2: afterprint event listener
  useEffect(() => {
    const afterPrint = () => setPrintingVenueId(null);
    window.addEventListener('afterprint', afterPrint);
    return () => window.removeEventListener('afterprint', afterPrint);
  }, []);

  async function fetchVenues() {
    try {
      const data = await apiClient<VenueItem[]>('/venues');
      setVenues(data);
    } catch {
      setVenues([]);
    }
  }

  useEffect(() => {
    fetchVenues();
  }, []);

  // Assign a venue to the currently-selected event.
  async function assignToEvent(venue: VenueItem) {
    if (!selectedEventId) return;
    setAssigningId(venue.id);
    try {
      await apiClient(`/events/${selectedEventId}`, { method: 'PATCH', body: { venueId: venue.id } });
      await Promise.all([fetchVenues(), refreshEvents()]);
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      alert(apiError.message || 'Could not assign this venue to the event.');
    } finally {
      setAssigningId(null);
    }
  }

  // The venue the selected event currently uses (if any).
  const currentEventVenueId = selectedEvent?.venue?.id ?? selectedEvent?.venueId ?? null;

  function openCreate() {
    setForm(emptyForm);
    setEditingId(null);
    setFormError('');
    setShowModal(true);
  }

  function openEdit(venue: VenueItem) {
    setForm({
      name: venue.name,
      address: venue.address,
      city: venue.city,
      state: venue.state,
      capacity: String(venue.capacity),
      latitude: venue.latitude != null ? String(venue.latitude) : '',
      longitude: venue.longitude != null ? String(venue.longitude) : '',
      website: venue.website || '',
      contactName: venue.contactName || '',
      contactPhone: venue.contactPhone || '',
      contactEmail: venue.contactEmail || '',
      parkingInfo: venue.parkingInfo || '',
      parkingImageUrl: venue.parkingImageUrl || '',
    });
    setEditingId(venue.id);
    setFormError('');
    setShowModal(true);
  }

  async function handleDelete(venue: VenueItem) {
    const eventCount = venue._count?.events || venue.events?.length || 0;

    if (eventCount > 0) {
      const eventNames = venue.events?.map((e) => e.name).join(', ') || `${eventCount} event(s)`;
      alert(
        `Cannot delete "${venue.name}" because it has associated events: ${eventNames}.\n\nPlease reassign or cancel those events first.`,
      );
      return;
    }

    if (!confirm(`Are you sure you want to delete "${venue.name}"?`)) {
      return;
    }

    try {
      await apiClient(`/venues/${venue.id}`, { method: 'DELETE' });
      fetchVenues();
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      alert(apiError.message || 'Failed to delete venue.');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');

    if (!form.name || !form.address || !form.city || !form.state || !form.capacity) {
      setFormError('All fields are required.');
      return;
    }

    const capacity = parseInt(form.capacity, 10);
    if (isNaN(capacity) || capacity < 1 || capacity > 100000) {
      setFormError('Capacity must be between 1 and 100,000.');
      return;
    }

    // Geolocation is optional, but if provided both must be valid and in range.
    const latStr = form.latitude.trim();
    const lngStr = form.longitude.trim();
    let latitude: number | undefined;
    let longitude: number | undefined;
    if (latStr !== '' || lngStr !== '') {
      if (latStr === '' || lngStr === '') {
        setFormError('Please provide both latitude and longitude, or leave both blank.');
        return;
      }
      latitude = Number(latStr);
      longitude = Number(lngStr);
      if (isNaN(latitude) || latitude < -90 || latitude > 90) {
        setFormError('Latitude must be a number between -90 and 90.');
        return;
      }
      if (isNaN(longitude) || longitude < -180 || longitude > 180) {
        setFormError('Longitude must be a number between -180 and 180.');
        return;
      }
    }

    setLoading(true);

    const payload = {
      name: form.name,
      address: form.address,
      city: form.city,
      state: form.state,
      capacity,
      latitude,
      longitude,
      website: form.website.trim() || undefined,
      contactName: form.contactName.trim() || undefined,
      contactPhone: form.contactPhone.trim() || undefined,
      contactEmail: form.contactEmail.trim() || undefined,
      parkingInfo: form.parkingInfo.trim() || undefined,
      parkingImageUrl: form.parkingImageUrl.trim() || undefined,
    };

    try {
      if (editingId) {
        await apiClient(`/venues/${editingId}`, { method: 'PATCH', body: payload });
      } else {
        await apiClient('/venues', { method: 'POST', body: payload });
      }
      setShowModal(false);
      fetchVenues();
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      setFormError(apiError.message || 'Failed to save venue.');
    } finally {
      setLoading(false);
    }
  }

  const printingVenue = printingVenueId ? venues.find((v) => v.id === printingVenueId) : null;

  return (
    <div style={styles.page}>
      {/* Task 5.1: Print CSS media query */}
      {printingVenueId && (
        <style>{`
          @media print {
            body * {
              visibility: hidden;
            }
            #printable-venue-detail, #printable-venue-detail * {
              visibility: visible;
            }
            #printable-venue-detail {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
          }
        `}</style>
      )}

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Venues</h1>
          <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
            Your workspace’s venues.{' '}
            {selectedEvent
              ? <>Assign one to <strong>{selectedEvent.name}</strong> with “Use for this event”.</>
              : <>Select an event to assign a venue to it.</>}
          </p>
        </div>
        {canEdit && <button style={styles.addButton} onClick={openCreate}>+ Add Venue</button>}
      </div>

      {venues.length === 0 && (
        <p style={{ color: '#666' }}>No venues yet. Click "Add Venue" to create one.</p>
      )}

      {venues.map((venue) => {
        const isExpanded = expandedVenueId === venue.id;
        return (
          <div key={venue.id} style={{ marginBottom: '0.75rem' }}>
            {/* Task 3.1: Click handler on venue card */}
            <div
              style={{
                ...styles.venueCard,
                ...(isExpanded ? { borderRadius: '6px 6px 0 0', marginBottom: 0 } : {}),
              }}
              onClick={() => toggleVenueExpansion(venue.id)}
            >
              <div style={styles.venueHeader}>
                <div>
                  <div style={styles.venueName}>{venue.name}</div>
                  <div style={styles.venueMeta}>
                    {venue.address}, {venue.city}, {venue.state}
                  </div>
                  <div style={styles.venueMeta}>
                    Capacity: {venue.capacity.toLocaleString()}
                  </div>
                  {/* Org-facing venue intelligence: upcoming/hosted split,
                      avg reach, capacity fit, and next/last event. */}
                  <VenueStatStrip venue={venue} />
                </div>
                <div style={styles.actions}>
                  {/* Assign-to-event: only when an event is selected */}
                  {canEdit && selectedEventId && (
                    currentEventVenueId === venue.id ? (
                      <span style={styles.currentVenueBadge} title={`Current venue for ${selectedEvent?.name}`}>
                        ✓ This event’s venue
                      </span>
                    ) : (
                      <button
                        style={styles.useBtn}
                        disabled={assigningId === venue.id}
                        onClick={(e) => { e.stopPropagation(); assignToEvent(venue); }}
                        title={`Use ${venue.name} for ${selectedEvent?.name}`}
                      >
                        {assigningId === venue.id ? 'Assigning…' : 'Use for this event'}
                      </button>
                    )
                  )}
                  {/* Task 3.1: stopPropagation on Edit/Delete */}
                  {canEdit && <button style={styles.editBtn} onClick={(e) => { e.stopPropagation(); openEdit(venue); }}>Edit</button>}
                  {canEdit && <button style={styles.deleteBtn} onClick={(e) => { e.stopPropagation(); handleDelete(venue); }}>Delete</button>}
                </div>
              </div>
              {/* Task 5.1: Contact info section */}
              {(venue.website || venue.latitude != null || venue.contactName || venue.contactPhone || venue.contactEmail || venue.parkingInfo) && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.6 }}>
                  {(venue.website || venue.latitude != null) && (
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' as const, marginBottom: '0.2rem' }}>
                      {venue.website && (
                        <span>
                          🌐 <a
                            href={/^https?:\/\//i.test(venue.website) ? venue.website : `https://${venue.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1a6b3c', textDecoration: 'none' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {venue.website.replace(/^https?:\/\//i, '')}
                          </a>
                        </span>
                      )}
                      {venue.latitude != null && venue.longitude != null && (
                        <span>
                          📍 <a
                            href={`https://www.google.com/maps/search/?api=1&query=${venue.latitude},${venue.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1a6b3c', textDecoration: 'none' }}
                            onClick={(e) => e.stopPropagation()}
                            title={`${venue.latitude}, ${venue.longitude}`}
                          >
                            View on map
                          </a>
                        </span>
                      )}
                    </div>
                  )}
                  {(venue.contactName || venue.contactPhone) && (
                    <div>
                      📞 {venue.contactName ? `Contact: ${venue.contactName}` : ''}
                      {venue.contactName && venue.contactPhone ? ' · ' : ''}
                      {venue.contactPhone && !venue.contactName ? `Contact: ${venue.contactPhone}` : ''}
                      {venue.contactPhone && venue.contactName ? venue.contactPhone : ''}
                    </div>
                  )}
                  {venue.contactEmail && (
                    <div>
                      ✉️ <a href={`mailto:${venue.contactEmail}`} style={{ color: '#1a6b3c', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>{venue.contactEmail}</a>
                    </div>
                  )}
                  {venue.parkingInfo && (
                    <div>🅿️ Parking: {venue.parkingInfo}</div>
                  )}
                </div>
              )}
            </div>
            {/* Task 3.1: Conditionally render detail panel */}
            {isExpanded && (
              <VenueDetailPanel venue={venue} onPrint={() => handlePrint(venue.id)} />
            )}
          </div>
        );
      })}

      {/* Task 5.1: Printable component rendered when printing */}
      {printingVenue && <PrintableVenueDetail venue={printingVenue} />}

      {/* Modal */}
      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>{editingId ? 'Edit Venue' : 'Add Venue'}</h2>

            {formError && <div style={styles.error}>{formError}</div>}

            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueName">Name</label>
                <input
                  id="venueName"
                  style={styles.input}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueAddress">Street Address</label>
                <input
                  id="venueAddress"
                  style={styles.input}
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label} htmlFor="venueCity">City</label>
                  <input
                    id="venueCity"
                    style={styles.input}
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                  />
                </div>
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label} htmlFor="venueState">State</label>
                  <input
                    id="venueState"
                    style={styles.input}
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueCapacity">Capacity</label>
                <input
                  id="venueCapacity"
                  type="number"
                  min="1"
                  max="100000"
                  style={styles.input}
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueWebsite">Website</label>
                <input
                  id="venueWebsite"
                  type="url"
                  style={styles.input}
                  maxLength={500}
                  placeholder="https://venue.example.com"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </div>

              {/* Location / Geolocation Section */}
              <h3 style={{ color: '#1a6b3c', fontSize: '1rem', margin: '1.25rem 0 0.25rem', borderTop: '1px solid #e0e0e0', paddingTop: '1rem' }}>
                📍 Location
              </h3>
              <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
                Optional map coordinates. Tip: in Google Maps, right-click the venue and
                click the lat/long at the top to copy them. Used for map links and future
                “places of interest nearby”.
              </p>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label} htmlFor="venueLatitude">Latitude</label>
                  <input
                    id="venueLatitude"
                    type="number"
                    step="any"
                    style={styles.input}
                    placeholder="e.g. 33.7490"
                    value={form.latitude}
                    onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                  />
                </div>
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label} htmlFor="venueLongitude">Longitude</label>
                  <input
                    id="venueLongitude"
                    type="number"
                    step="any"
                    style={styles.input}
                    placeholder="e.g. -84.3880"
                    value={form.longitude}
                    onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                  />
                </div>
              </div>

              {/* Venue Contact Section */}
              <h3 style={{ color: '#1a6b3c', fontSize: '1rem', margin: '1.25rem 0 0.75rem', borderTop: '1px solid #e0e0e0', paddingTop: '1rem' }}>
                Venue Contact
              </h3>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueContactName">Contact Name</label>
                <input
                  id="venueContactName"
                  style={styles.input}
                  maxLength={200}
                  placeholder="Contact person name"
                  value={form.contactName}
                  onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueContactPhone">Contact Phone</label>
                <input
                  id="venueContactPhone"
                  style={styles.input}
                  maxLength={20}
                  placeholder="Phone number"
                  value={form.contactPhone}
                  onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueContactEmail">Contact Email</label>
                <input
                  id="venueContactEmail"
                  type="email"
                  style={styles.input}
                  maxLength={200}
                  placeholder="Email address"
                  value={form.contactEmail}
                  onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueParkingInfo">Parking Info</label>
                <textarea
                  id="venueParkingInfo"
                  style={{ ...styles.input, resize: 'vertical' as const }}
                  rows={3}
                  placeholder="Parking details..."
                  value={form.parkingInfo}
                  onChange={(e) => setForm({ ...form, parkingInfo: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueParkingImage">Parking Map Image</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    id="venueParkingImage"
                    type="text"
                    style={{ ...styles.input, flex: 1 }}
                    placeholder="Image URL or upload below..."
                    value={form.parkingImageUrl}
                    onChange={(e) => setForm({ ...form, parkingImageUrl: e.target.value })}
                  />
                </div>
                <input
                  type="file"
                  accept="image/*"
                  style={{ marginTop: '6px', fontSize: '0.8rem' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      if (typeof reader.result === 'string') {
                        setForm((prev) => ({ ...prev, parkingImageUrl: reader.result as string }));
                      }
                    };
                    reader.readAsDataURL(file);
                  }}
                />
                {form.parkingImageUrl && (
                  <div style={{ marginTop: '8px' }}>
                    <img src={form.parkingImageUrl} alt="Parking map preview" style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: '6px', border: '1px solid #e0e0e0' }} />
                    <button type="button" onClick={() => setForm({ ...form, parkingImageUrl: '' })} style={{ display: 'block', marginTop: '4px', background: 'none', border: 'none', color: '#d32f2f', fontSize: '0.75rem', cursor: 'pointer' }}>✕ Remove image</button>
                  </div>
                )}
              </div>

              <button type="submit" style={styles.submitBtn} disabled={loading}>
                {loading ? 'Saving...' : editingId ? 'Update Venue' : 'Create Venue'}
              </button>
              <button type="button" style={styles.cancelBtn} onClick={() => setShowModal(false)}>
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
